/*!
  Pioneer WFRIA DisclosureList v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["DisclosureList"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("DisclosureList") < 0) {
    window.__PIONEER_GLOBAL__.components.push("DisclosureList");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import PropTypes from "prop-types";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { KEYBOARD_KEYS } from "../common/constants";
import { useControlOrUnControl, useUniqueId } from "../common/hooks";
import { invalidIf, requiredIf } from "../common/proptypes";
import { __uniqueID, getFocusableElements } from "../common/utilities";
import { LiveRegion } from "../private";
import { DisclosureListContainerContext } from "./DisclosureListContainer/DisclosureListContainerContext";
import STRINGS from "./strings";
import { DisclosureListItem, IconArrowClose, IconArrowOpen, Section } from "..";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const CLEAR_ANNOUNCEMENT_TIMEOUT = 500;
/**
 * DisclosureList is a React component that provides an expandable and
 * collapsible vertical list of Buttons. When a Button is activated,
 * content associated with it is rendered at a provided location.
 */

function DisclosureList(_ref) {
  let {
    "aria-labelledby": ariaLabelledby,
    children,
    customStrings,
    header,
    footer,
    defaultSelectedIndex,
    onClick,
    defaultExpanded,
    expanded: propsExpanded,
    title,
    onExpandedChange,
    disabled
  } = _ref;
  const ctx = useContext(DisclosureListContainerContext);
  const isMounted = useRef(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [message, setMessage] = useState("");
  const disclosureListRef = useRef();
  const childrenRefs = useRef([]);
  const contentRef = useRef();
  const disclosureListId = useUniqueId();
  const childrenListId = useUniqueId("pnr-disclosurelist__section-list");
  /**
   * useControlOrUnControl hook returns the getter and setter
   * methods for updating the prop value.
   */

  const [getExpanded, setExpanded] = useControlOrUnControl({
    unControlledPropValue: defaultExpanded,
    controlledPropValue: propsExpanded,
    changeHandler: onExpandedChange
  });

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);
  /**
   * Retrieves a possible next DisclosureList in the DisclosureListContainer.
   */


  const getNextDisclosureList = useCallback(() => {
    var _disclosureListRef$cu, _disclosureListRef$cu2;

    return (_disclosureListRef$cu = disclosureListRef.current.closest("li")) === null || _disclosureListRef$cu === void 0 ? void 0 : (_disclosureListRef$cu2 = _disclosureListRef$cu.nextElementSibling) === null || _disclosureListRef$cu2 === void 0 ? void 0 : _disclosureListRef$cu2.firstElementChild;
  }, []);
  /**
   * Retrieves a possible previous DisclosureList in the DisclosureListContainer.
   */

  const getPreviousDisclosureList = useCallback(() => {
    var _disclosureListRef$cu3, _disclosureListRef$cu4;

    return (_disclosureListRef$cu3 = disclosureListRef.current.closest("li")) === null || _disclosureListRef$cu3 === void 0 ? void 0 : (_disclosureListRef$cu4 = _disclosureListRef$cu3.previousElementSibling) === null || _disclosureListRef$cu4 === void 0 ? void 0 : _disclosureListRef$cu4.firstElementChild;
  }, []);
  /**
   * Retrieves the content container from the parent DisclosureListContainer.
   * Throws an error if the DisclosureListContainer is not present.
   */

  const getContentContainer = useCallback(() => {
    const disclosureListContainer = disclosureListRef.current.closest(".pnr-disclosure-list-container");

    if (!disclosureListContainer) {
      throw new Error("DisclosureList must be used as a child of DisclosureListContainer.");
    } else {
      return disclosureListContainer.querySelector(".pnr-disclosure-list-container__content");
    }
  }, []);
  /**
   * Moves focus to the expand/collapse button in the next DisclosureList
   * in the DisclosureListContainer.
   */

  const moveToNextToggleButton = useCallback(() => {
    var _getNextDisclosureLis;

    const nextSiblingToggleButton = (_getNextDisclosureLis = getNextDisclosureList()) === null || _getNextDisclosureLis === void 0 ? void 0 : _getNextDisclosureLis.querySelector(".pnr-section__toggle button");

    if (nextSiblingToggleButton) {
      nextSiblingToggleButton.focus();
    } else {
      var _focusableElements$in2;

      // Move to next focusable element outside of DisclosureListContainer.
      const focusableElements = getFocusableElements(document);
      const indexOfContentArea = focusableElements.findIndex(item => item === contentRef.current);
      let indexToFocus = indexOfContentArea + 1;
      const focusableElementsLength = focusableElements.length;

      while (indexToFocus < focusableElementsLength && ((_focusableElements$in = focusableElements[indexToFocus]) === null || _focusableElements$in === void 0 ? void 0 : _focusableElements$in.closest(".pnr-disclosure-list-container__content")) !== null) {
        var _focusableElements$in;

        indexToFocus++;
      }

      (_focusableElements$in2 = focusableElements[indexToFocus]) === null || _focusableElements$in2 === void 0 ? void 0 : _focusableElements$in2.focus();
    }
  }, [getNextDisclosureList]);
  /**
   * Focuses this DisclosureList's toggle button.
   */

  const focusToggleButton = () => {
    const toggleButton = disclosureListRef.current.querySelector(".pnr-section__toggle button");
    toggleButton && toggleButton.focus();
  };
  /**
   * Handles up and down focus moves from menu item buttons.
   */


  const onItemUpOrDownKey = useCallback((index, upOrDown) => {
    if (upOrDown && index === 0) {
      var _firstItemButton$clos, _firstItemButton$clos2;

      // Move up from first menu item button to expand/collapse button.
      const firstItemButton = childrenRefs.current[0].getButtonRef();
      (_firstItemButton$clos = firstItemButton.closest("section")) === null || _firstItemButton$clos === void 0 ? void 0 : (_firstItemButton$clos2 = _firstItemButton$clos.firstElementChild.querySelector("button")) === null || _firstItemButton$clos2 === void 0 ? void 0 : _firstItemButton$clos2.focus();
    } else if (upOrDown && index > 0) {
      // Move up to previous menu item button if DisclosureList is expanded.
      if (getExpanded()) {
        childrenRefs.current[index - 1].focus();
      } else {
        focusToggleButton();
      }
    } else if (!upOrDown && index < childrenRefs.current.length - 1) {
      // Move to next menu item button if DisclosureList is expanded.
      if (getExpanded()) {
        childrenRefs.current[index + 1].focus();
      } else {
        focusToggleButton();
      }
    } else {
      // Move to next DisclosureList if it exists.
      moveToNextToggleButton();
    }
  }, [moveToNextToggleButton, getExpanded]);
  /**
   * Handles key events for the toggle button.
   */

  const handleToggleKeyDown = event => {
    const {
      key,
      shiftKey
    } = event;

    if (key === KEYBOARD_KEYS.UP || key === KEYBOARD_KEYS.TAB && shiftKey) {
      // Move to previous DisclosureList's footer or last menu button if the list is open.
      let previousListBody;
      const previousList = getPreviousDisclosureList();

      if (previousList) {
        previousListBody = previousList.querySelector(".pnr-section__body");
      }

      if (previousListBody && previousListBody.classList.contains("pnr-expandable--expanded")) {
        event.preventDefault();
        const prevSiblingItems = previousList.querySelectorAll(".pnr-disclosure-list--item");
        const lastItemOfPreviousList = prevSiblingItems[prevSiblingItems.length - 1];

        if (lastItemOfPreviousList.className.indexOf("pnr-disclosure-list--footer") !== -1) {
          lastItemOfPreviousList.focus();
        } else {
          lastItemOfPreviousList.firstElementChild.focus();
        }
      }
    } else if (key === KEYBOARD_KEYS.DOWN || key === KEYBOARD_KEYS.TAB && !shiftKey) {
      event.preventDefault(); // Move to first child menu item.

      if (getExpanded()) {
        childrenRefs.current[0].focus();
      } else {
        // This DisclosureLists's list is closed. Move to next list's expand/collapse button.
        moveToNextToggleButton();
      }
    } else if (key === KEYBOARD_KEYS.RIGHT) {
      setExpanded(true);
    } else if (key === KEYBOARD_KEYS.LEFT) {
      setExpanded(false);
    }
  };
  /**
   * Handles tab key events in the content area.
   */


  const handleContentKeyDown = useCallback(event => {
    const {
      key,
      shiftKey,
      target,
      currentTarget
    } = event;

    if (key === KEYBOARD_KEYS.TAB && activeIndex !== null) {
      const focusableElements = getFocusableElements(currentTarget);

      if (!shiftKey && (target === focusableElements[focusableElements.length - 1] || focusableElements.length === 0 && target === contentRef.current)) {
        /**
         * For a tab press without shift key, move to the next item button if the
         * last focusable elements or there are no focusable elements.
         */
        event.stopPropagation();
        event.preventDefault();
        onItemUpOrDownKey(activeIndex, false);
      } else if (shiftKey && target === contentRef.current) {
        /**
         * For a tab press with shift key, move to the item button for this content.
         */
        event.stopPropagation();
        event.preventDefault();
        onItemUpOrDownKey(activeIndex + 1, true);
      }
    }
  }, [activeIndex, contentRef, onItemUpOrDownKey]);
  /**
   * Handles click events from menu item buttons.
   */

  const handleMenuItemClick = useCallback((id, index) => {
    const childProps = React.Children.toArray(children)[index].props; // For controlled component:

    if (onClick) {
      const renderId = __uniqueID(); // Set an id on the content element to prevent overwrites.


      contentRef.current.setAttribute("data-render-id", renderId);
      /**
       * Pass render function to parent that will set content in state
       * if the content element id is corresponding.
       */

      const renderFunction = content => {
        if (renderId === contentRef.current.getAttribute("data-render-id")) {
          ctx.onContentChange(content, childProps["aria-label"], disclosureListId);
        }
      };

      onClick(id, renderFunction);
    } else {
      ctx.onContentChange(childProps.children, childProps["aria-label"], disclosureListId);
    }

    setMessage(`${childProps["aria-label"]} ${componentStrings.detailsShown}`);
    setActiveIndex(index);
    isMounted && isMounted.current && contentRef.current.focus();
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [children, onClick, disclosureListId, ctx]);
  /**
   * Handles key events on menu items.
   */

  const handleMenuItemKeyDown = useCallback((event, id, index, footer) => {
    const {
      key,
      shiftKey
    } = event;

    if (key === KEYBOARD_KEYS.UP || key === KEYBOARD_KEYS.TAB && shiftKey) {
      event.preventDefault();
      event.stopPropagation();

      if (key === KEYBOARD_KEYS.TAB && activeIndex !== null && index === activeIndex + 1) {
        /**
         * If the previous menu button's content is shown,
         * focus on last focusable element of previous menu button's content or
         * the content area if there are no focusable elements.
         */
        const focusableElements = getFocusableElements(contentRef.current);

        if (focusableElements.length > 0) {
          focusableElements[focusableElements.length - 1].focus();
        } else {
          contentRef.current.focus();
        }
      } else {
        onItemUpOrDownKey(index, true);
      }
    } else if (key === KEYBOARD_KEYS.DOWN || key === KEYBOARD_KEYS.TAB && !shiftKey) {
      event.stopPropagation();
      event.preventDefault(); // Focus on this item's content area if it is shown.

      if (key === KEYBOARD_KEYS.TAB && index === activeIndex) {
        contentRef.current.focus();
      } else {
        onItemUpOrDownKey(index, false);
      }
    } else if (key === KEYBOARD_KEYS.SPACE) {
      event.preventDefault(); // Don't activate Footer.

      if (index < React.Children.toArray(children).length) {
        handleMenuItemClick(id, index);
      }
    } else if (key === KEYBOARD_KEYS.ENTER && footer) {
      const anchors = event.target.querySelectorAll("a");

      if (anchors.length > 0) {
        anchors[0].click();
      }
    }
  }, [activeIndex, onItemUpOrDownKey, children, handleMenuItemClick]);
  /**
   * Adds an event listener to parent DisclosureListContainer content area.
   * Saves the content area element in a ref. Adding and removing on each
   * change of expanded state so this state is fresh.
   */

  const isExpanded = getExpanded();
  useEffect(() => {
    const contentContainer = getContentContainer();
    contentContainer.addEventListener("keydown", handleContentKeyDown);
    contentRef.current = contentContainer;
    return () => contentContainer.removeEventListener("keydown", handleContentKeyDown);
  }, [getContentContainer, handleContentKeyDown, isExpanded]);
  /**
   * Listen for changes in the DisclosureListContainer context for the id of
   * the DisclosureList that has the active content. If has changed to a
   * different DisclosureList, reset the active index of this DisclosureList.
   */

  useEffect(() => {
    if (disclosureListId !== (ctx === null || ctx === void 0 ? void 0 : ctx.activeDisclosureListId)) {
      setActiveIndex(null);
    }
  }, [ctx === null || ctx === void 0 ? void 0 : ctx.activeDisclosureListId, disclosureListId]);
  /**
   * Set active index and initial content based on defaultSelectedIndex prop.
   */

  useEffect(() => {
    if (defaultSelectedIndex !== null) {
      const babies = React.Children.toArray(children);

      if (isNaN(defaultSelectedIndex) || defaultSelectedIndex < 0 || defaultSelectedIndex > babies.length - 1) {
        throw new Error(`"defaultSelectedIndex" prop must be a number between 0 and one less than the number of children.`);
      }

      setActiveIndex(defaultSelectedIndex); // For controlled component, handle as if the corresponding menu button had been clicked.

      if (onClick) {
        handleMenuItemClick(babies[defaultSelectedIndex].props.itemId, defaultSelectedIndex);
      } else {
        const childProps = babies[defaultSelectedIndex].props;
        ctx.onContentChange(childProps.children, childProps["aria-label"]);
      }
    } // To prevent focusing the content area on load if any item is selected by default.


    contentRef && (isMounted.current = true); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * Creates clones of children DisclosureListItems with appropriate props
   * and adds optional footer.
   */

  const getClonedChildren = () => {
    const childClones = React.Children.map(children, (child, index) => {
      return _jsx("li", {
        children: /*#__PURE__*/React.cloneElement(child, {
          ref: ref => childrenRefs.current[index] = ref,
          index,
          activeIndex,
          onClick: handleMenuItemClick,
          onKeyDown: handleMenuItemKeyDown,
          customStrings
        })
      });
    });

    if (footer) {
      const index = childClones.length;
      childClones.push(_jsx("li", {
        children: _jsx(DisclosureListItem, {
          label: footer,
          ref: ref => childrenRefs.current[index] = ref,
          index: index,
          activeIndex: activeIndex,
          onKeyDown: handleMenuItemKeyDown,
          customStrings: customStrings,
          footer: true
        }, "footer")
      }, "footer"));
    }

    return childClones;
  };

  return _jsxs("div", {
    ref: disclosureListRef,
    className: "pnr-disclosure-list",
    children: [_jsx(Section, {
      "aria-labelledby": ariaLabelledby,
      customStrings: customStrings,
      header: header,
      expanded: getExpanded(),
      defaultExpanded: defaultExpanded,
      title: title,
      onExpandedChange: setExpanded,
      onToggleButtonKeyDown: handleToggleKeyDown,
      disabled: disabled,
      iconExpand: _jsx(IconArrowClose, {
        size: "xsmall"
      }),
      iconCollapse: _jsx(IconArrowOpen, {
        size: "xsmall"
      }),
      kind: "disclosureList",
      "aria-controls": childrenListId,
      children: _jsx("ul", {
        className: "wf-list-unstyled",
        "aria-label": `${title} ${componentStrings.listAriaLabelAddition}`,
        id: childrenListId,
        "aria-hidden": !getExpanded(),
        children: getClonedChildren()
      })
    }), _jsx(LiveRegion, {
      politeness: "assertive",
      clearAnnouncementDelay: CLEAR_ANNOUNCEMENT_TIMEOUT,
      "data-testid": "disclosure-list-live-region",
      children: message
    })]
  });
}

DisclosureList.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Array of DisclosureListItems */
  children: PropTypes.node.isRequired,

  /** Title of DisclosureList, if `header` is not used. It is the tooltip for expand/collapse button. */
  title: PropTypes.string.isRequired,

  /**
   * If only `title` is provided, `aria-labelledby` will be used as the heading id.
   * Required when using `header`.
   */
  ["aria-labelledby"]: requiredIf(PropTypes.string, (props, propName) => props.header && !props[propName], "when 'header' is supplied."),

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /**
     * A function that returns the message for the expanded section.
     * This message is set inside a visually hidden node, to be read by the
     * screen reader, when expand button is triggered.
     * It receives ${title} as an argument.
     * Defaults to `${title}, expanded`
     */
    expandedAnnouncement: PropTypes.func,

    /**
     * A function that returns the message for the collapsed section.
     * This message is set inside a visually hidden node, to be read by the
     * screen reader, when expand button is triggered.
     * It receives ${title} as an argument.
     * Defaults to `${title}, collapsed`
     */
    collapsedAnnouncement: PropTypes.func,

    /**
     * aria-label for Expandable button.
     * This function will receive two arguments.
     * First argument is the section "${title}".
     * Second argument is a boolean value for the expanded state:
     * <ul>
     *  <li>
     *    `title`: Section title.
     *  </li>
     *  <li>
     *    `expanded`: Section expanded state.
     *  </li>
     * </ul>
     */
    expandButtonLabel: PropTypes.func,

    /**
     * Added to the aria-label of a DisclosureListItem for the announcement
     * when its content is displayed. Defaults to ", selected"
     */
    detailsShown: PropTypes.string,

    /**
     *  Added to the aria-label for the unordered list of menu items.
     *  Defaults to "children"
     */
    listAriaLabelAddition: PropTypes.string,

    /**
     * Added to the aria-label of a DisclosureListItem with content displayed.
     */
    selected: PropTypes.string
  }),

  /** Default value for Expanded when component is uncontrolled. */
  defaultExpanded: invalidIf(PropTypes.bool, (props, propName) => {
    return props[propName] && props.expanded;
  }, "when `expanded` prop is provided."),

  /** For setting initial selected item. */
  defaultSelectedIndex: PropTypes.number,

  /** Disable or enable the look of a button. */
  disabled: PropTypes.bool,

  /** Prop for Collapse or Expand Section when component is controlled. */
  expanded: PropTypes.bool,

  /** Footer that will appear below the last DisclosureListItem. */
  footer: PropTypes.node,

  /** Content of expand / collapse button. Use `header` instead of `title` when HTML is required. */
  header: PropTypes.node,

  /**
   * Callback to be executed when a menu item is clicked in controlled mode.
   * This will be passed the id of the DisclosureListItem and a render function
   * that can be used to render content to the content area.
   */
  onClick: PropTypes.func,

  /** Callback to be invoked on expand/collapse. */
  onExpandedChange: PropTypes.func
} : {};
DisclosureList.defaultProps = {
  customStrings: null,
  defaultSelectedIndex: 0,
  "aria-label": "",
  footer: null,
  onClick: null,
  header: null,
  expanded: null,
  defaultExpanded: false,
  children: null,
  disabled: false,
  "aria-labelledby": null,
  onExpandedChange: null
};
export default DisclosureList;